import { serviceTicketsColumns } from '@/constants/app/serviceTickets/serviceTickets';
import {
  useBulkAssignServiceTicketsToRouteMutation,
  useGetRouteQuery,
} from '@/store/api/services/operations/routes/index';
import { useGetServiceTicketsQuery } from '@/store/api/services/operations/serviceTickets/index';
import { useGetUsersAthenaQuery } from '@/store/api/services/users/users';
import type { ModalChildrenProps } from '@bitstopco/bitstop-theme';
import { TableWrapper } from '@bitstopco/bitstop-theme';
import { LoadingButton } from '@mui/lab';
import { Box, Button, DialogActions, DialogContent, Divider, Typography } from '@mui/material';
import { skipToken } from '@reduxjs/toolkit/query';
import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';

interface ManageRouteServiceTicketsProps extends ModalChildrenProps {
  routeId?: string;
}

const ManageRouteServiceTickets = ({ hide, props }: ManageRouteServiceTicketsProps) => {
  const routeId = props?.routeId;
  const [payload, setPayload] = useState<any>();
  const [multiSelectValues, setMultiSelectValues] = useState<boolean[]>([]);
  // Tickets that failed in the last bulk attempt. We keep the modal open and
  // re-select these once the succeeded ones drop out of the list, so the
  // operator can retry without re-finding them.
  const [failedIds, setFailedIds] = useState<Set<string>>(new Set());
  const [bulkAssignServiceTicketsToRoute, { isLoading }] =
    useBulkAssignServiceTicketsToRouteMutation();

  // Fetch route data to get current service tickets
  const { data: routeData } = useGetRouteQuery(routeId || '', {
    skip: !routeId,
  });

  // Get current service ticket IDs in the route
  const currentTicketIds = useMemo(() => {
    const routeView = routeData?.data as any;
    const tickets = routeView?.service_tickets || [];
    // Create a Set with both string and number versions of IDs to handle type mismatches
    const idSet = new Set<string | number>();
    tickets.forEach((t: any) => {
      if (t.id) {
        idSet.add(t.id);
        // Also add string and number versions to handle type mismatches
        idSet.add(String(t.id));
        if (!Number.isNaN(Number(t.id))) {
          idSet.add(Number(t.id));
        }
      }
    });
    console.log('Current route ticket IDs:', Array.from(idSet));
    return idSet;
  }, [routeData]);

  // Fetch technicians for name lookup
  const { data: techniciansData } = useGetUsersAthenaQuery({ role_id: 5 });

  // Create technicians map
  const techniciansMap = useMemo(() => {
    const map = new Map<
      string | number,
      { id?: string | number; first_name?: string; last_name?: string; email?: string }
    >();
    if (techniciansData?.data) {
      techniciansData.data.forEach((technician) => {
        if (technician.id) {
          map.set(technician.id, {
            id: technician.id,
            first_name: technician.first_name,
            last_name: technician.last_name,
            email: technician.email,
          });
        }
      });
    }
    return map;
  }, [techniciansData]);

  // Fetch service tickets
  const { data: serviceTicketsData, isFetching } = useGetServiceTicketsQuery(payload || skipToken);

  // Filter out tickets already in the route
  const serviceTickets = useMemo(() => {
    const allTickets = serviceTicketsData?.data || [];
    const filtered = allTickets.filter((ticket) => {
      // Check both string and number versions of the ID
      const ticketId = ticket.id;
      const isAlreadyAssigned =
        currentTicketIds.has(ticketId) ||
        currentTicketIds.has(String(ticketId)) ||
        (!Number.isNaN(Number(ticketId)) && currentTicketIds.has(Number(ticketId)));

      if (isAlreadyAssigned) {
        console.log(`Filtering out ticket ${ticketId} (already in route)`);
      }
      return !isAlreadyAssigned;
    });
    console.log(`Filtered ${allTickets.length} tickets to ${filtered.length} available tickets`);
    return filtered;
  }, [serviceTicketsData?.data, currentTicketIds]);

  // Reset multi-select when the visible tickets change. Tickets that failed the
  // last bulk attempt stay selected; everything else resets to unselected.
  useEffect(() => {
    if (serviceTickets.length !== multiSelectValues.length) {
      setMultiSelectValues(serviceTickets.map((ticket) => failedIds.has(String(ticket.id))));
    }
  }, [serviceTickets, multiSelectValues.length, failedIds]);

  const handleSubmit = async () => {
    if (!routeId) {
      toast.error('Route ID is required');
      return;
    }

    const selectedTicketIds = multiSelectValues
      .map((isSelected, index) => isSelected && serviceTickets[index]?.id)
      .filter(Boolean) as string[];

    if (selectedTicketIds.length === 0) {
      toast.error('Please select at least one service ticket');
      return;
    }

    try {
      // One request assigns every selected ticket; the response reports
      // per-ticket success/failure so we can surface partial results.
      const response = await bulkAssignServiceTicketsToRoute({
        route_id: routeId,
        service_ticket_ids: selectedTicketIds,
      }).unwrap();

      const results = response?.data?.results ?? [];
      const failed = results.filter((result) => !result.success);

      if (failed.length === 0) {
        setFailedIds(new Set());
        toast.success(
          `Successfully assigned ${selectedTicketIds.length} service ticket(s) to route`,
        );
        hide();
        return;
      }

      // Partial (or total) failure: keep the modal open and remember the failed
      // tickets so the reset effect re-selects them for a retry.
      setFailedIds(new Set(failed.map((result) => String(result.service_ticket_id))));

      if (failed.length === selectedTicketIds.length) {
        toast.error('Failed to assign service tickets to route');
      } else {
        toast.error(
          `Assigned ${selectedTicketIds.length - failed.length} of ${selectedTicketIds.length}; ${failed.length} failed`,
        );
      }
    } catch (error: any) {
      toast.error(error?.data?.message || 'Failed to assign service tickets to route');
      console.error('Error assigning service tickets to route:', error);
    }
  };

  const selectedCount = multiSelectValues.filter(Boolean).length;

  return (
    <>
      <DialogContent sx={{ my: 2, p: 0 }}>
        <Box sx={{ p: 2, pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            Add Service Tickets to Route
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Select service tickets to add to this route. Tickets already assigned to this route are
            excluded.
          </Typography>
        </Box>

        <Box sx={{ px: 2, pb: 2 }}>
          <TableWrapper
            data={serviceTickets}
            columns={serviceTicketsColumns(techniciansMap)}
            isLoading={isFetching || !payload}
            totalPages={serviceTicketsData?.meta?.pagination?.total_pages || 1}
            totalItems={serviceTickets.length}
            onChange={setPayload}
            multiSelectValues={multiSelectValues}
            onMultiSelectValuesChange={setMultiSelectValues}
            sortByDefaultValue="created_at"
            dateRangeFilterKey="created_at"
            tableProps={{
              placeholder: {
                empty: {
                  title: 'No service tickets found',
                  content:
                    'No service tickets available to add. All tickets may already be assigned to this route.',
                },
              },
            }}
          />
        </Box>

        {selectedCount > 0 && (
          <Box sx={{ px: 2, pb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {selectedCount} service ticket(s) selected
            </Typography>
          </Box>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
        <Button variant="text" onClick={hide}>
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={selectedCount === 0}
          loading={isLoading}
        >
          Add {selectedCount > 0 ? `${selectedCount} ` : ''}Ticket{selectedCount !== 1 ? 's' : ''}
        </LoadingButton>
      </DialogActions>
    </>
  );
};

export default ManageRouteServiceTickets;
